/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from "react";
import {
  GridApi,
  GridApiPremium,
  GridColDef,
  GridColumnGroup,
  GridFilterItem,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { TFunction } from "i18next";

import {
  ChildMixUpdate,
  Latest,
  MaterialRead,
  Next,
  MixMaterial,
  SingleMixSummary,
  SteelGrade,
  UnitType,
} from "src/store/api/generatedApi";

import { MixLayerState } from "./layerEditor/types";
import {
  AdditionRow,
  BasketRow,
  EmptyBasketRow,
  EmptyProfileRow,
  Material,
  ProfileRow,
  RatioRow,
  Row,
  TotalRow,
} from "./types";
import {
  getMaterialMass,
  totalBasketMass,
  isMixHeaderRow,
  removeMaterialLayers,
  scaleMaterialLayersToTotal,
  updateRowEventParam,
} from "./utils";

import { BaseSteelGradesCell } from "./BaseSteelGradesCell";
import { DRICell } from "./DriCell";
import { EmptyWeightCell } from "./EmptyWeightCell";
import { Detail, MIX_SUMMARY_WIDTH, MixSummaryCell } from "./MixSummaryCell";
import { OverrideCell } from "./OverrideCell";
import { PopoverSteelGradesCell } from "./PopoverSteelGradesCell";
import { SupervisorActionCell } from "./SupervisorActionCell";
import { SupervisorSteelGradesCell } from "./SupervisorSteelGradesCell";
import {
  basketWeightEmptyCellClassName,
  basketWeightHeaderClassName,
  basketWeightTotalCellClassName,
  basketWeightUsedCellClassName,
  copperGroupCellClassName,
  copperGroupHeaderClassName,
  driWeightCellClassName,
  driWeightHeaderClassName,
  grabTypeHeaderClassName,
  materialHeaderClassName,
  materialTotalCellClassName,
  materialWeightCellClassName,
  mixDiffCellClassName,
  mixDiffHeaderClassName,
  mixSummaryCellClassName,
  mixSummaryHeaderClassName,
  nameOverrideCellClassName,
  nameOverrideHeaderClassName,
  numHeatsCellClassName,
  numHeatsHeaderClassName,
  popoverSteelGradesCellClassName,
  popoverSteelGradesHeaderClassName,
  subStandardGroupCellClassName,
  subStandardGroupHeaderClassName,
  supervisorSteelGradesCellClassName,
  supervisorSteelGradesHeaderClassName,
  warnedSteelGradesCellClassName,
  warnedSteelGradesHeaderClassName,
} from "./sx";
import { WarnedSteelGradesCell } from "./WarnedSteelGradesCell";
import { WeightCell } from "./WeightCell";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { SplitMixButton } from "./SplitMix";

const mixBucketRows = (
  api: GridApiPremium,
  mixId: number
): (ProfileRow | BasketRow | AdditionRow)[] =>
  api
    .getAllRowIds()
    .map((rowId) => api.getRow<Row>(rowId))
    .filter(
      (row): row is ProfileRow | BasketRow | AdditionRow =>
        row !== null &&
        (row.type === "basket" ||
          row.type === "addition" ||
          row.type === "profile") &&
        row.mixId === mixId
    );

const computeCopperPercentMaxTolerance = (
  steelGrades: SteelGrade[]
): number => {
  const copperConstraints = steelGrades.flatMap((grade) =>
    grade.chemical_constraints.filter((constraint) =>
      constraint.coefficients.some(
        (coefficient) => coefficient.chemical_element.name === "copper"
      )
    )
  );
  if (copperConstraints.length === 0) return 0;
  return Math.min(
    ...copperConstraints.map((constraint) => constraint.weight_percent_max)
  );
};

export const mixDiff = (
  deploymentType: "standard" | "edited_standard" | "backup" | "edited_backup",
  originalDeploymentMixIds: Record<number, number>,
  planTimeframe: Latest | Next,
  startAt: Date,
  endAt: Date
): GridColDef<Row> => ({
  field: "mix_name",
  width: 120,
  headerName: "#",
  valueGetter: (_, row): number => {
    return row.mixId;
  },
  renderCell: ({ row }: GridRenderCellParams<Row, number>) => {
    if (
      (row.type === "profile" || row.type === "empty_profile") &&
      row.planId !== null
    ) {
      const { planId, mixId, originalPrice } = row;
      const originalDeploymentMixId = originalDeploymentMixIds[mixId];
      return (
        <SupervisorActionCell
          planId={planId}
          mixId={mixId}
          originalDeploymentMixId={originalDeploymentMixId}
          deploymentType={deploymentType}
          startAt={startAt}
          endAt={endAt}
          planTimeframe={planTimeframe}
          originalPrice={originalPrice ?? 0}
        />
      );
    } else {
      return null;
    }
  },
  cellClassName: mixDiffCellClassName.toString(),
  headerClassName: mixDiffHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

const recipeIdentityFields: ReadonlySet<string> = new Set([
  "mix_name",
  "num_heats",
  "steel_grades",
  "grouping",
  "name_override",
  "dri_weight",
]);

const isCollapsedHeaderRow = (
  row: Row,
  collapsedMixIds: ReadonlySet<number>
): boolean => isMixHeaderRow(row) && collapsedMixIds.has(row.mixId);

const totalRowFor = (row: Row): TotalRow => ({
  id: row.id,
  type: "total",
  steelGrades: row.steelGrades,
  mixId: row.mixId,
  mixName: row.mixName,
  period: row.period,
});

export const hideCollapsedValues = (
  column: GridColDef<Row>,
  collapsedMixIds: ReadonlySet<number>
): GridColDef<Row> => {
  if (recipeIdentityFields.has(column.field)) return column;
  const { renderCell } = column;
  return {
    ...column,
    valueGetter: (value, row, columnDef, apiRef) => {
      if (!column.valueGetter) {
        return isCollapsedHeaderRow(row, collapsedMixIds) ? null : value;
      }
      const source = isCollapsedHeaderRow(row, collapsedMixIds)
        ? totalRowFor(row)
        : row;
      return column.valueGetter(value, source, columnDef, apiRef) as unknown;
    },
    ...(renderCell
      ? {
          renderCell: (params: GridRenderCellParams<Row>) =>
            isCollapsedHeaderRow(params.row, collapsedMixIds)
              ? renderCell({ ...params, row: totalRowFor(params.row) })
              : renderCell(params),
        }
      : {}),
  };
};

export const collapseHostField = "mix_name";

export const withCollapseToggle = (
  column: GridColDef<Row>,
  translator: TFunction,
  collapsedMixIds: ReadonlySet<number>,
  onToggleCollapse: (mixId: number) => void
): GridColDef<Row> => {
  const { renderCell } = column;
  return {
    ...column,
    filterOperators: [
      {
        value: "notCollapsed",
        getApplyFilterFn: (item: GridFilterItem) => {
          const collapsed = new Set((item.value ?? []) as number[]);
          return (_value, row: Row) =>
            isMixHeaderRow(row) || !collapsed.has(row.mixId);
        },
      },
    ],
    renderCell: (params: GridRenderCellParams<Row>) => {
      const { row } = params;
      const content = renderCell?.(params) ?? null;
      if (!isMixHeaderRow(row)) {
        return content;
      }
      const isCollapsed = collapsedMixIds.has(row.mixId);
      const label = isCollapsed
        ? translator("showDetails")
        : translator("hideDetails");
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={0.5}
        >
          <Tooltip title={label}>
            <IconButton
              size="small"
              aria-label={label}
              sx={{ padding: 0 }}
              onClick={() => {
                onToggleCollapse(row.mixId);
              }}
            >
              {isCollapsed ? (
                <KeyboardArrowRightIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {content}
        </Stack>
      );
    },
    disableColumnMenu: true,
  };
};

export const mixSummary = (
  unitFormatter: (unit: UnitType) => string,
  details: Detail[],
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>
): GridColDef<Row> => ({
  field: "mix_name",
  width: MIX_SUMMARY_WIDTH,
  headerName: details
    .map((detail): string => {
      switch (detail) {
        case "cu":
          return `Cu ${unitFormatter("mass_fraction")}`;
        case "price_diff":
          return unitFormatter("specific_cost");
        default:
          throw new Error();
      }
    })
    .join(" | "),
  valueGetter: (_, row): number => {
    return row.mixId;
  },
  renderCell: (row: GridRenderCellParams<Row, number>) => {
    if (
      (row.row.type === "profile" || row.row.type === "empty_profile") &&
      row.row.originalPrice !== null &&
      row.row.planId !== null
    ) {
      return (
        <MixSummaryCell
          key={[row.row.mixId, row.row.planId].join(".")}
          originalCost={row.row.originalPrice}
          planId={row.row.planId}
          period={row.row.period}
          mixId={row.row.mixId}
          details={details}
          preloadedSummary={preloadedSummaries?.[row.row.mixId]}
        />
      );
    } else {
      return null;
    }
  },
  cellClassName: mixSummaryCellClassName.toString(),
  headerClassName: mixSummaryHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const numHeats = (
  doSubmit?: (updates: ChildMixUpdate[], comment: string) => Promise<void>,
  disableEdit?: boolean,
  hasEdited?: boolean
): GridColDef<Row> => ({
  field: "num_heats",
  headerName: "#",
  width: 50,
  type: "number" as const,
  valueGetter: (_, row: Row) => {
    switch (row.type) {
      case "empty_profile":
      case "profile":
        return row.numberOfHeats;
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  renderCell: (row: GridRenderCellParams<Row, number>) => {
    if (row.row.type === "profile" || row.row.type === "empty_profile") {
      const { mixId, name, baskets, steelGradeProduction, description } =
        row.row;
      return (
        <Stack alignItems="center">
          <Typography component="span" variant="body1Mono">
            {row.row.numberOfHeats.toFixed(0)}
          </Typography>
          {doSubmit === undefined ||
          disableEdit === undefined ||
          hasEdited === undefined ? null : (
            <SplitMixButton
              mixId={mixId}
              name={name}
              description={description}
              steelGradeProduction={steelGradeProduction}
              baskets={baskets}
              doSubmit={doSubmit}
              disabled={hasEdited || disableEdit}
            />
          )}
        </Stack>
      );
    } else {
      return null;
    }
  },
  rowSpanValueGetter: (_, row: Row) => row.mixId,
  cellClassName: numHeatsCellClassName.toString(),
  headerClassName: numHeatsHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const nameOverride = (
  translator: TFunction,
  options: string[],
  disableEdit: boolean,
  mixIdsRequiringNameOverride: Set<number>
): GridColDef<Row> => ({
  field: "name_override",
  headerName: translator("nameOverride"),
  width: 120,
  valueGetter: (_, row): number => row.mixId,
  renderCell: ({ row }: GridRenderCellParams<Row, number>) => {
    switch (row.type) {
      case "profile":
      case "empty_profile":
      case "ratio":
        if (row.planId === null) return null;
        return (
          <OverrideCell
            planId={row.planId}
            period={row.period}
            mixId={row.mixId}
            options={options}
            disabled={disableEdit}
            required={mixIdsRequiringNameOverride.has(row.mixId)}
          />
        );
      case "basket":
      case "empty_basket":
      case "addition":
      case "total":
        return null;
    }
  },
  rowSpanValueGetter: (_, row) => row.mixId,
  cellClassName: nameOverrideCellClassName.toString(),
  headerClassName: nameOverrideHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

const groupingBase: Omit<
  GridColDef<Row>,
  | "headerName"
  | "valueFormatter"
  | "cellClassName"
  | "headerClassName"
  | "width"
> = {
  field: "grouping",
  rowSpanValueGetter: (_, row): number => {
    switch (row.type) {
      case "basket":
      case "addition":
      case "ratio":
      case "total":
      case "empty_profile":
      case "empty_basket":
      case "profile": {
        return row.mixId;
      }
    }
  },
  sortable: false,
  headerAlign: "center",
  resizable: false,
};

export const copperGroup: GridColDef<Row> = {
  ...groupingBase,
  headerName: "Cu",
  width: 50,
  valueFormatter: (_, row) => {
    switch (row.type) {
      case "empty_profile":
      case "profile":
      case "ratio":
        return (
          computeCopperPercentMaxTolerance(row.steelGrades) * 100
        ).toFixed(0);
      case "basket":
      case "addition":
      case "empty_basket":
      case "total":
        return null;
    }
  },
  cellClassName: copperGroupCellClassName.toString(),
  headerClassName: copperGroupHeaderClassName.toString(),
};

export const subStandardGroup = (translator: TFunction): GridColDef<Row> => ({
  ...groupingBase,
  headerName: translator("productionGroupShort"),
  width: 70,
  valueFormatter: (_, row) => {
    switch (row.type) {
      case "empty_profile":
      case "profile":
      case "ratio":
        return row.name;
      case "basket":
      case "addition":
      case "empty_basket":
      case "total":
        return null;
    }
  },
  cellClassName: subStandardGroupCellClassName.toString(),
  headerClassName: subStandardGroupHeaderClassName.toString(),
});

export const driWeight = (
  translator: TFunction,
  massUnit: UnitType,
  massScaleFactor: number,
  unitFormatter: (unit: UnitType) => string,
  preloadedDris?: Record<number, MixMaterial | null | undefined>,
  loading = false
): GridColDef<Row> => {
  const driStr = translator("dri");
  return {
    field: "dri_weight",
    headerName: `${driStr} ${unitFormatter(massUnit)}`,
    width: 55,
    valueGetter: (_, row): number => {
      switch (row.type) {
        case "basket":
        case "profile":
        case "addition":
        case "ratio":
        case "empty_basket":
        case "empty_profile":
        case "total": {
          return row.mixId;
        }
      }
    },
    renderCell: ({ row }: GridRenderCellParams<Row>) => {
      switch (row.type) {
        case "empty_profile":
        case "profile":
          return (
            <DRICell
              mixId={row.mixId}
              planId={row.planId}
              period={row.period}
              massScaleFactor={massScaleFactor}
              loading={loading}
              preloadedDri={preloadedDris?.[row.mixId]}
            />
          );
        case "basket":
        case "addition":
        case "ratio":
        case "empty_basket":
        case "total":
          return null;
      }
    },
    headerClassName: driWeightHeaderClassName.toString(),
    cellClassName: driWeightCellClassName.toString(),
    sortable: false,
    headerAlign: "center",
    resizable: false,
  };
};

export const material = (
  material: Material,
  massScaleFactor: number,
  disableEdit: boolean,
  numberFormatter: (value: number) => string,
  roundingUnits: number,
  percentageFormatter?: (value: number) => string
): GridColDef<Row> => {
  const { name: materialName } = material;
  const isPercentageMode = percentageFormatter !== undefined;
  return {
    field: material.id.toString(),
    headerName: material.name,
    headerClassName: materialHeaderClassName.toString(),
    minWidth: 50,
    flex: 1,
    type: "number",
    valueGetter: (_, row, __, dataGridApi) => {
      switch (row.type) {
        case "empty_basket":
        case "empty_profile":
          return null;
        case "basket":
        case "addition":
        case "ratio":
        case "profile": {
          const value = getMaterialMass(row.layers, materialName);
          if (isPercentageMode) {
            return value === 0 ? null : value;
          }
          return value !== 0 ? value * massScaleFactor : null;
        }
        case "total": {
          const value = mixBucketRows(
            dataGridApi.current as GridApiPremium,
            row.mixId
          ).reduce(
            (total, rowData) =>
              total + getMaterialMass(rowData.layers, materialName),
            0
          );

          if (isPercentageMode) {
            return value === 0 ? null : value;
          }
          return value !== 0 ? value * massScaleFactor : null;
        }
      }
    },
    valueFormatter: (value, row, _column, dataGridApi) => {
      if (value === null || value === undefined) {
        return null;
      }

      if (percentageFormatter) {
        let basketTotal = 0;

        if (row.type === "total") {
          basketTotal = mixBucketRows(
            dataGridApi.current as GridApiPremium,
            row.mixId
          ).reduce((sum, rowData) => sum + totalBasketMass(rowData.layers), 0);
        } else if (
          row.type === "basket" ||
          row.type === "profile" ||
          row.type === "addition" ||
          row.type === "ratio"
        ) {
          basketTotal = totalBasketMass(row.layers);
        }

        return percentageFormatter(
          basketTotal > 0 ? (value / basketTotal) * 100 : 0
        );
      }

      const shouldPrependPlus = row.type === "addition" && value > 0;
      const formattedValue = numberFormatter(value);
      return shouldPrependPlus ? `+${formattedValue}` : formattedValue;
    },
    rowSpanValueGetter: () => null,
    editable: !disableEdit,
    valueSetter: (value, row) => {
      switch (row.type) {
        case "empty_basket":
        case "empty_profile":
        case "total": {
          return row;
        }
        case "addition": {
          if (value !== undefined && value !== null && value >= 0) {
            const massValue = isPercentageMode
              ? Number(value)
              : Number(value) / massScaleFactor;
            return {
              ...row,
              layers: scaleMaterialLayersToTotal(
                row.layers,
                materialName,
                massValue,
                "grab",
                roundingUnits
              ),
            };
          }
          return {
            ...row,
            layers: removeMaterialLayers(row.layers, materialName),
          };
        }
        case "ratio": {
          if (value !== undefined && value !== null && value >= 0) {
            const massValue = isPercentageMode
              ? Number(value)
              : Number(value) / massScaleFactor;
            return {
              ...row,
              layers: scaleMaterialLayersToTotal(
                row.layers,
                materialName,
                massValue,
                "ratio",
                roundingUnits
              ),
            };
          }
          return {
            ...row,
            layers: removeMaterialLayers(row.layers, materialName),
          };
        }
        case "basket":
        case "profile": {
          if (value !== undefined && value !== null && value >= 0) {
            const massValue = isPercentageMode
              ? Number(value)
              : Number(value) / massScaleFactor;
            return {
              ...row,
              layers: scaleMaterialLayersToTotal(
                row.layers,
                materialName,
                massValue,
                "mass",
                roundingUnits
              ),
            };
          }
          return {
            ...row,
            layers: removeMaterialLayers(row.layers, materialName),
          };
        }
      }
    },
    cellClassName: ({ row }) => {
      const { type } = row;
      switch (type) {
        case "total": {
          return materialTotalCellClassName.toString();
        }
        case "empty_profile":
        case "empty_basket":
          return "";
        case "basket":
        case "addition":
        case "ratio":
        case "profile": {
          return materialWeightCellClassName.toString();
        }
      }
    },
    sortable: false,
    headerAlign: "center",
    resizable: false,
  };
};

const makeBasketWeightRowCreator =
  (api: GridApi) =>
  (
    row:
      | ProfileRow
      | BasketRow
      | EmptyProfileRow
      | EmptyBasketRow
      | AdditionRow
      | RatioRow
  ) => {
    api.updateRows([row]);
    api.publishEvent("stateChange", updateRowEventParam);
  };

export interface LayerEditingHooks {
  onEditLayers: (mixId: number) => void;
  hasLayerEdits: (mixId: number) => boolean;
}

export const basketWeight = (
  translator: TFunction,
  massUnit: UnitType,
  massScaleFactor: number,
  numberFormatter: (value: number) => string,
  unitFormatter: (unit: UnitType) => string,
  disableEdit: boolean,
  displayUnitType: UnitType = massUnit,
  layerEditing?: LayerEditingHooks
): GridColDef<Row> => {
  const displayScaleFactor =
    displayUnitType === "mass_fraction" ? 1 : massScaleFactor;

  return {
    field: "basket_weight",
    headerName: `${translator("basket")} ${unitFormatter(massUnit)}`,
    width: 100,
    headerClassName: basketWeightHeaderClassName.toString(),
    renderCell: (
      rowParams: GridRenderCellParams<Row, number | null>
    ): React.JSX.Element | string | null => {
      const { row, value, api } = rowParams;
      const rowCreator = makeBasketWeightRowCreator(api as GridApiPremium);
      switch (row.type) {
        case "empty_basket": {
          const { steelGrades, mixId, mixName, basketNumber, id, period } = row;
          return (
            <EmptyWeightCell
              onClick={() => {
                const next: BasketRow = {
                  type: "basket",
                  steelGrades,
                  mixId,
                  mixName,
                  basketNumber,
                  id,
                  period,
                  layers: [],
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
            />
          );
        }
        case "empty_profile": {
          const {
            name,
            baskets,
            steelGrades,
            mixId,
            mixName,
            id,
            period,
            numberOfHeats,
            originalPrice,
            steelGradeProduction,
            planId,
            description,
          } = row;
          return (
            <EmptyWeightCell
              onClick={() => {
                const next: ProfileRow = {
                  type: "profile",
                  layers: [],
                  name,
                  baskets,
                  steelGrades,
                  mixId,
                  mixName,
                  id,
                  period,
                  numberOfHeats,
                  originalPrice,
                  steelGradeProduction,
                  planId,
                  description,
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
            />
          );
        }
        case "total": {
          const formatted =
            value !== null && value !== undefined && value !== 0
              ? numberFormatter(value * displayScaleFactor)
              : null;
          if (layerEditing === undefined) {
            return formatted;
          }
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              {formatted !== null ? (
                <Typography variant="body1Mono">{formatted}</Typography>
              ) : (
                <span />
              )}
              <IconButton
                data-testid={`edit-mix-layers-${row.mixId}`}
                disabled={disableEdit}
                aria-label={translator("editLayers")}
                sx={{ padding: 0 }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  layerEditing.onEditLayers(row.mixId);
                }}
                color={
                  layerEditing.hasLayerEdits(row.mixId) ? "primary" : "default"
                }
              >
                <LayersOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        }
        case "profile": {
          const {
            id,
            mixId,
            mixName,
            name,
            baskets,
            steelGrades,
            period,
            numberOfHeats,
            originalPrice,
            steelGradeProduction,
            planId,
            description,
          } = row;
          return (
            <WeightCell
              onClick={() => {
                const next: EmptyProfileRow = {
                  type: "empty_profile",
                  id,
                  planId,
                  name,
                  baskets,
                  steelGrades,
                  mixId,
                  mixName,
                  originalPrice,
                  basketNumber: 1,
                  steelGradeProduction,
                  period,
                  numberOfHeats,
                  description,
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
              formattedWeight={numberFormatter(
                (value ?? 0) * displayScaleFactor
              )}
            />
          );
        }
        case "ratio": {
          const { id, mixId, mixName, steelGrades, period, planId } = row;
          return (
            <WeightCell
              onClick={() => {
                const next: RatioRow = {
                  id,
                  type: "ratio",
                  steelGrades,
                  mixId,
                  mixName,
                  period,
                  layers: [],
                  numberOfHeats: 1,
                  originalPrice: null,
                  name: "",
                  steelGradeProduction: [],
                  planId,
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
              formattedWeight={numberFormatter(
                (value ?? 0) * displayScaleFactor
              )}
            />
          );
        }
        case "addition": {
          const { id, mixId, mixName, steelGrades, basketNumber, period } = row;
          return (
            <WeightCell
              onClick={() => {
                const next: AdditionRow = {
                  id,
                  type: "addition",
                  steelGrades,
                  mixId,
                  mixName,
                  basketNumber,
                  period,
                  layers: [],
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
              formattedWeight={numberFormatter(
                (value ?? 0) * displayScaleFactor
              )}
            />
          );
        }
        case "basket": {
          const { id, mixId, mixName, steelGrades, basketNumber, period } = row;
          return (
            <WeightCell
              onClick={() => {
                const next: EmptyBasketRow = {
                  id,
                  type: "empty_basket",
                  steelGrades,
                  mixId,
                  mixName,
                  basketNumber,
                  period,
                };
                rowCreator(next);
              }}
              disabled={disableEdit}
              formattedWeight={numberFormatter(
                (value ?? 0) * displayScaleFactor
              )}
            />
          );
        }
      }
    },
    valueGetter: (_, row, __, dataGridApi) => {
      switch (row.type) {
        case "basket":
        case "profile":
          return totalBasketMass(row.layers);
        case "addition":
        case "ratio":
        case "empty_basket":
        case "empty_profile":
          return null;
        case "total":
          return dataGridApi.current
            .getAllRowIds()
            .map((rowId) => dataGridApi.current.getRow<Row>(rowId))
            .filter(
              (
                dataGridRow: Row | null
              ): dataGridRow is ProfileRow | BasketRow =>
                dataGridRow !== null &&
                (dataGridRow.type === "profile" ||
                  dataGridRow.type === "basket")
            )
            .filter(({ mixId }) => mixId === row.mixId)
            .reduce(
              (sum, dataGridRow) => sum + totalBasketMass(dataGridRow.layers),
              0
            );
      }
    },
    valueFormatter: (value: number) => {
      if (value !== undefined) {
        return numberFormatter(value * displayScaleFactor);
      }
    },
    rowSpanValueGetter: () => null,
    cellClassName: (cellParams) => {
      const {
        row: { type },
      } = cellParams;
      switch (type) {
        case "empty_basket":
        case "empty_profile": {
          return basketWeightEmptyCellClassName.toString();
        }
        case "total": {
          return basketWeightTotalCellClassName.toString();
        }
        case "basket":
        case "addition":
        case "ratio":
        case "profile":
          return basketWeightUsedCellClassName.toString();
      }
    },
    sortable: false,
    headerAlign: "center",
    resizable: false,
  };
};

const baseSteelGradesColumn = (
  translator: TFunction,
  width: number
): GridColDef<Row> => ({
  field: "steel_grades",
  headerName: translator("steelGrades"),
  width,
  filterOperators: [
    {
      label: "Contains",
      value: "contains",
      getApplyFilterFn: (item: { value?: number[] }) => {
        return (steelGradeIds: number[]) => {
          return (
            item.value === undefined ||
            item.value?.length === 0 ||
            steelGradeIds.some((steelGradeId) =>
              item.value?.includes(steelGradeId)
            )
          );
        };
      },
    },
  ],
  valueGetter: (_, row): number[] => {
    switch (row.type) {
      case "basket":
      case "total":
      case "empty_basket":
      case "empty_profile":
      case "addition":
      case "ratio":
      case "profile": {
        return row.steelGrades.map((steelGrade) => steelGrade.id);
      }
    }
  },
  rowSpanValueGetter: (_, row) => row.mixId,
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const simpleSteelGrades = (
  translator: TFunction,
  width: number,
  selectedSteelGradeIds: number[]
): GridColDef<Row> => ({
  ...baseSteelGradesColumn(translator, width),
  renderCell: ({ row }) => {
    switch (row.type) {
      case "empty_profile":
      case "profile": {
        return (
          <BaseSteelGradesCell
            steelGrades={row.steelGrades}
            selectedSteelGradeIds={selectedSteelGradeIds}
          />
        );
      }
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  cellClassName: popoverSteelGradesCellClassName.toString(),
  headerClassName: popoverSteelGradesHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const warnedSteelGrades = (
  translator: TFunction,
  width: number,
  selectedSteelGradeIds: number[],
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>
): GridColDef<Row> => ({
  ...baseSteelGradesColumn(translator, width),
  renderCell: ({ row }) => {
    switch (row.type) {
      case "empty_profile":
      case "profile": {
        if (row.planId === null) return null;
        return (
          <WarnedSteelGradesCell
            planId={row.planId}
            period={row.period}
            mixId={row.mixId}
            steelGrades={row.steelGrades}
            selectedSteelGradeIds={selectedSteelGradeIds}
            description={row.description}
            preloadedSummary={preloadedSummaries?.[row.mixId]}
          />
        );
      }
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  cellClassName: warnedSteelGradesCellClassName.toString(),
  headerClassName: warnedSteelGradesHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const supervisorSteelGrades = (
  translator: TFunction,
  width: number,
  selectedSteelGradeIds: number[]
): GridColDef<Row> => ({
  ...baseSteelGradesColumn(translator, width),
  renderCell: ({ row }) => {
    switch (row.type) {
      case "empty_profile":
      case "profile": {
        if (row.planId === null) return null;
        return (
          <SupervisorSteelGradesCell
            planId={row.planId}
            mixId={row.mixId}
            steelGrades={row.steelGrades}
            selectedSteelGradeIds={selectedSteelGradeIds}
            description={row.description}
          />
        );
      }
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  cellClassName: supervisorSteelGradesCellClassName.toString(),
  headerClassName: supervisorSteelGradesHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const plotSelectSteelGrades = (
  translator: TFunction,
  width: number,
  selectedSteelGradeIds: number[],
  focusedSteelGradeId: number,
  onSteelGradeClick: (id: number) => void,
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>
): GridColDef<Row> => ({
  ...baseSteelGradesColumn(translator, width),
  renderCell: ({ row }) => {
    switch (row.type) {
      case "empty_profile":
      case "profile": {
        if (row.planId === null) return null;
        return (
          <WarnedSteelGradesCell
            onClick={onSteelGradeClick}
            steelGrades={row.steelGrades}
            selectedSteelGradeIds={selectedSteelGradeIds}
            focusedSteelGradeId={focusedSteelGradeId}
            planId={row.planId}
            period={row.period}
            mixId={row.mixId}
            preloadedSummary={preloadedSummaries?.[row.mixId]}
          />
        );
      }
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  cellClassName: popoverSteelGradesCellClassName.toString(),
  headerClassName: popoverSteelGradesHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export interface PopoverLayerEditor {
  baseMassScaleFactor: number;
  materials: MaterialRead[];
  getLayerStateForMix: (mixId: number) => MixLayerState | null;
  onMixLayerStateChange: (
    mixId: number,
    mixUpdates: ChildMixUpdate[],
    hasMixEdits: boolean
  ) => void;
  isMixManuallyLayered: (mixId: number) => boolean;
  onResetMixLayers: (mixId: number) => void;
}

export const popoverSteelGrades = (
  translator: TFunction,
  width: number,
  selectedSteelGradeIds: number[],
  materials: Material[],
  hasDri: boolean,
  columnGroupingModel: GridColumnGroup[],
  disableEdit: boolean,
  setMixDescription?: (mixId: number, description: string) => void,
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>,
  preloadedDris?: Record<number, MixMaterial | null | undefined>,
  layerEditor?: PopoverLayerEditor
): GridColDef<Row> => ({
  ...baseSteelGradesColumn(translator, width),
  renderCell: ({ row }) => {
    switch (row.type) {
      case "empty_profile":
      case "profile": {
        if (row.planId === null) return null;
        return (
          <PopoverSteelGradesCell
            planId={row.planId}
            period={row.period}
            steelGrades={row.steelGrades}
            selectedSteelGradeIds={selectedSteelGradeIds}
            materials={materials}
            mixId={row.mixId}
            hasDri={hasDri}
            columnGroupingModel={columnGroupingModel}
            disableEdit={disableEdit}
            description={row.description}
            setDescription={
              setMixDescription === undefined
                ? undefined
                : (description: string) => {
                    setMixDescription(row.mixId, description);
                  }
            }
            preloadedSummary={preloadedSummaries?.[row.mixId]}
            preloadedDri={preloadedDris?.[row.mixId]}
            layerEditor={layerEditor}
          />
        );
      }
      case "basket":
      case "empty_basket":
      case "addition":
      case "ratio":
      case "total":
        return null;
    }
  },
  cellClassName: popoverSteelGradesCellClassName.toString(),
  headerClassName: popoverSteelGradesHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
});

export const grabType = (translator: TFunction): GridColDef<Row> => ({
  field: "grabType",
  headerName: translator("grabType"),
  width: 150,
  headerClassName: grabTypeHeaderClassName.toString(),
  sortable: false,
  headerAlign: "center",
  resizable: false,
  valueFormatter: (_, row): string | null => {
    switch (row.type) {
      case "addition":
        return translator("addition");
      case "ratio":
        return translator("ratio");
      default:
        return null;
    }
  },
});
